import type { StateDisplay, StateSlug } from './lib/stateCore.mjs';

export interface HydratedPhoto {
  url: string;
  width: number;
  height: number;
  /**
   * Gallery photo index (0-based), carried through the aspect-sort so a
   * responsive `srcset` can be built per photo without parsing `url`. Optional
   * — `url` remains the identity (React key / error-map key); this is additive.
   */
  index?: number;
}

export interface Gym {
  id: string;
  stableId?: string | undefined;
  name: string;
  addressLine1: string;
  suburb: string;
  /** Display form (e.g. 'NSW'). Widened from the "NSW" literal in R3 for multi-state. */
  state: StateDisplay;
  postcode: string;
  lat: number;
  lng: number;
  allStyles: string[];
  ageGroups: string[];
  experienceLevels: string[];
  scheduleSummary?: string | undefined;
  websiteUrl?: string | undefined;
  phoneNumber?: string | undefined;
  openingHours?: string[]; // Google Places weekday descriptions
  about?: string;
  sourceUrl?: string;
  // ⚰️ UI-144 (operator-ruled 2026-08-28) — `isAutoListed` stood here and is GONE. Its
  // only readers were three `{gym?.isAutoListed && …}` guards on the gym page, and the
  // operator ruled the "Auto-sourced" badge STANDARD on every gym page, so the guards
  // were unwrapped and the field lost every reader. It was never real data either:
  // absent from all 2,750 display documents, hardcoded true by the search projection,
  // and defaulted `?? true` by both the SSG bake and the page itself.
  // ⚰️ UI-131 slice 4 (operator-ruled 2026-08-26) — `lastCheckedAt` and `lastHydratedAt`
  // stood here and are GONE. Both were dead: `lastCheckedAt` was FABRICATED on every
  // render with `new Date()` and read by nothing, ever; `lastHydratedAt` carried a real
  // pipeline stamp but lost its only reader when slice 1 removed the gym page's
  // "Information last verified" line.
  // ⚠️ NOTHING WAS DELETED FROM THE DATA. `lastHydratedAt` remains on
  // `display/{state}/gyms`, the pipeline keeps writing it, and it is still the source of
  // truth the /about freshness comment points at. Only this type stopped carrying it.
  // ⚠️ `GymDisplay` below has its OWN `lastHydratedAt` — a different interface, left alone.
  google_reviews_last_checked_at?: string;

  /**
   * Google Places attributes.
   * NOTE: These are ONLY for the base 'gyms' collection and hydration models.
   * They must NEVER be persisted in user subcollections (favourites).
   */
  google_place_id?: string | null | undefined;
  google_rating?: number | null; // used for sorting/initial view
  google_user_ratings_total?: number | null;
  photos?: (string | HydratedPhoto)[]; // Combined type to handle legacy and new data
}

export interface FilterState {
  styles: string[];
  ageGroups: string[];
  experienceLevels: string[];
  suburb: string;
  radius: number; // in km (used when useRadius is true)
  useRadius: boolean; // false = postcode-only, true = radius-based search
  gymName: string;
  // Phase 4: Canonical identifiers
  location_entry_id?: string | null;
  location_source?: "index" | "legacy" | null;
}

export enum ViewMode {
  LIST = 'LIST',
  MAP = 'MAP',
  SPLIT = 'SPLIT'
}

export interface Coordinates {
  lat: number;
  lng: number;
}

// Authentication Types
export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string | undefined;
  photoURL?: string | undefined;
  provider: 'google' | 'email';
  createdAt: string;
}


export interface PendingAction {
  type: 'favourite' | 'save-search';
  gymId: string;
  returnUrl: string;
  uiState?: {
    scrollPosition: number;
    mapCenter?: [number, number];
    filters?: FilterState;
  };
}

// Saved Search configuration
export interface SavedSearch {
  id: string;
  userId?: string; // Optional: implicit in subcollection path
  name: string;
  location: string;       // suburb or postcode
  /**
   * UI-01: the state the search was saved FROM (URL-slug form, e.g. 'vic').
   * Absent/null on documents saved before stamping — INCLUDING any saved from a
   * VIC page in the VIC-live/pre-UI-01 window, not only pre-multi-state ones.
   * Readers fall back to DEFAULT_STATE, which reproduces exactly how every such
   * document was applied before this fix (always against NSW) rather than
   * guessing; a null-state search saved from a VIC page therefore keeps
   * applying against NSW until re-saved. Deployed firestore.rules impose no
   * key restriction on savedSearches items, so the field is writable today.
   */
  state?: StateSlug | null;
  radius: number;
  useRadius: boolean;     // false = postcode-only, true = radius-based
  filters: {
    styles: string[];
    ageGroups: string[];
    experienceLevels: string[];
  };
  viewMode: ViewMode;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  // Phase 4: Canonical identifiers
  location_entry_id?: string | null;
  location_source?: "index" | "legacy" | null;
}


// Gym Enrichment Types (Firestore: enrichment/{state}/gyms/{gymId})
export interface GymEnrichmentTags {
  styles: string[];
  ageGroups: string[];
  experienceLevel: string[];
}

export interface GymEnrichmentConfidence {
  styles: number;
  ageGroups: number;
  experienceLevel: number;
}

export type EnrichmentStatus = 'ready' | 'pending' | 'error' | 'missing';

export interface GymEnrichmentMeta {
  modelVersion: string;
  generatedAt: Date;
  expiresAt: Date;
  status: EnrichmentStatus;
  error?: string;
}

export interface GymEnrichmentOverrides {
  tags?: Partial<GymEnrichmentTags>;
  updatedAt?: Date;
  updatedBy?: string;
}

export interface GymEnrichmentAudit {
  version: string;
  raw_text_hash: string;
  scoped_text_hash: string;
  validator_called: boolean;
  processed_at: Date;
  last_classified_at?: Date;
  last_seen_at?: Date;
}


export interface GymEnrichment {
  gymName: string;
  suburb?: string;
  postcode?: string;
  tags: GymEnrichmentTags;
  confidence: GymEnrichmentConfidence;
  matchedPhrases: string[];
  meta: GymEnrichmentMeta;
  overrides?: GymEnrichmentOverrides;
  audit?: GymEnrichmentAudit;
}


/**
 * Location selection payload for search
 */
export interface LocationSelection {
  /** Display form (e.g. 'NSW'). Widened from the "NSW" literal in R3 for multi-state. */
  state?: StateDisplay;
  suburb?: string;
  postcode: string;
  raw?: string;
  kind?: 'postcode' | 'suburb_postcode';
  entryId?: string;     // canonical id (preferred)
  lat?: number;
  lng?: number;
  display?: string;
  slug?: string;
  source?: "index" | "legacy";
}

/**
 * Legacy type used for location suggestions
 */
export interface NSWLocationRow {
  suburb: string;
  postcode: string;
}

/**
 * Gym Display document (Firestore: display/{state}/gyms/{placeId})
 * Pre-computed, search-ready gym data for DirectoryPage and Header search
 */
export interface GymDisplay {
  placeId: string;           // Document ID = Google Place ID
  stableId: string;          // Stable internal ID for URL construction (never changes)
  name: string;
  suburb: string;
  /** Display form (e.g. 'NSW'). Optional: pre-R3 docs lack it; readers default to NSW (R2 rule 5). */
  state?: StateDisplay;
  /** URL slug form (e.g. 'nsw'). Optional: stamped by sync-display from R3 onward. */
  stateSlug?: StateSlug;
  postcode: string;
  lat: number | null;        // Gym location for map pins
  lng: number | null;
  websiteUrl: string | null;

  // Pre-computed enrichment tags
  styles: string[];
  ageGroups: string[];
  experienceLevel: string[];

  // Saturated Places Metadata (New)
  formattedAddress?: string;
  rating?: number | null;
  userRatingCount?: number | null;
  phoneNumber?: string | null;
  openingHours?: string[] | null;
  photos?: HydratedPhoto[]; // Saturated photos from sync process

  // Metadata
  enrichedAt: Date | null;
  displayReady: true;
  syncedAt: Date;
  lastHydratedAt?: Date | null; // Track when Places data was last refreshed
  needsHeal?: boolean;         // Flag for proactive healing in daily sync

  // Search optimization
  nameLower: string;         // Lowercase name for text search
}

export type PlaceId = string;

/**
 * Firestore document structure for favourites/{userId}/items/{placeId}
 * Contains only the reference (placeId) and timestamp - no Google data.
 */
export interface FavouriteRefDoc {
  placeId: PlaceId;           // equals gym.id / google_place_id
  savedAt: any;               // Firestore Timestamp
}